/**
 * AgentFleetWidget — the compact "Your agents" card for Home (Dashboard)
 * and the Dev hub. Makes the deployed-agent fleet visible where the
 * operator starts their day: live counts (deployed / running / active now),
 * ledgered runs in the last 24h, and the most recently active agents with
 * their latest run narration — all read from the same append-only activity
 * ledger the Agents grid proves itself with (lib/agent/runHistory). One
 * click deep-links to Projects → Agents.
 *
 * Self-contained: subscribes to the agents + activity collections itself,
 * so hosts just render it with an `onOpen` deep-link callback.
 */

import { useEffect, useMemo, useState } from 'react';
import { Activity, ArrowUpRight, Bot, CircleDashed, ShieldCheck, Zap } from 'lucide-react';
import { GlassPanel } from './GlassPanel';
import { C } from '../lib/tokens';
import { subscribeAgents } from '../lib/db/agents';
import { subscribeActivity } from '../lib/db/activity';
import { fleetPulse, summarizeAgentRuns } from '../lib/agent/runHistory';
import * as argoWorker from '../lib/agent/argoWorker';
import type { ActivityEntry, DeployedAgent } from '../lib/db/types';

/** How many agents the compact list shows. */
const LIST_LIMIT = 3;

function fmtRelative(iso: string): string {
  try {
    const diff = Math.max(0, Date.now() - new Date(iso).getTime());
    const mins = Math.floor(diff / 60_000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  } catch {
    return iso;
  }
}

export function AgentFleetWidget({ onOpen }: { onOpen: () => void }) {
  const [agents, setAgents] = useState<DeployedAgent[]>([]);
  const [runs, setRuns] = useState<ActivityEntry[]>([]);
  // Re-evaluate the "active now" window every 30s so the live dot decays
  // honestly even when no new ledger rows arrive.
  const [nowTick, setNowTick] = useState(() => Date.now());

  useEffect(() => {
    const unsubAgents = subscribeAgents(setAgents);
    const unsubLedger = subscribeActivity((rows) =>
      setRuns(rows.filter((r) => r.event === 'agent_run')),
    );
    const timer = window.setInterval(() => setNowTick(Date.now()), 30_000);
    return () => {
      unsubAgents();
      unsubLedger();
      window.clearInterval(timer);
    };
  }, []);

  const pulse = useMemo(() => fleetPulse(agents, runs, { now: nowTick }), [agents, runs, nowTick]);
  const summaries = useMemo(() => summarizeAgentRuns(runs, { now: nowTick }), [runs, nowTick]);

  // Most recently active first; never-run agents trail, running before idle.
  const listed = useMemo(() => {
    const rank = (a: DeployedAgent) =>
      summaries[a.id]?.lastRunAt ?? (a.state === 'running' ? '0-running' : '0-idle');
    return [...agents].sort((a, b) => (rank(a) < rank(b) ? 1 : -1)).slice(0, LIST_LIMIT);
  }, [agents, summaries]);

  const workerConfigured = argoWorker.isConfigured();

  return (
    <GlassPanel variant="subtle" animate={false} style={{ padding: '16px 20px' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <span
            style={{
              width: 32,
              height: 32,
              borderRadius: 10,
              display: 'inline-grid',
              placeItems: 'center',
              flex: '0 0 auto',
              background: 'linear-gradient(135deg, rgba(255,0,255,0.22), rgba(187,65,152,0.12))',
              border: '1px solid rgba(255,255,255,0.14)',
              color: '#fff',
            }}
          >
            <Bot size={16} strokeWidth={1.9} />
          </span>
          <div style={{ minWidth: 0 }}>
            <span className="t-eyebrow">Agents</span>
            <h2 className="t-section-title" style={{ margin: '2px 0 0' }}>
              Your agents
            </h2>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {pulse.activeNow > 0 && (
            <span
              className="glass-pill glass-pill--green"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: 99,
                  background: C.green,
                  animation: 'pulse 1.6s ease-in-out infinite',
                }}
              />
              {pulse.activeNow} active now
            </span>
          )}
          <span
            className={workerConfigured ? 'glass-pill glass-pill--green' : 'glass-pill'}
            title={
              workerConfigured
                ? 'Runs are routed to the hosted Claude worker.'
                : 'Simulation mode — runs use the deterministic in-app engines.'
            }
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
          >
            {workerConfigured ? <Zap size={11} /> : <CircleDashed size={11} />}
            {workerConfigured ? 'Claude worker' : 'Simulation'}
          </span>
          <button className="glass-btn" onClick={onOpen} title="Open Projects → Agents">
            Open agents <ArrowUpRight size={12} aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* Fleet pulse line — real counts from the fleet + the ledger. */}
      <p className="t-meta" style={{ margin: '10px 0 0', color: C.textMuted }}>
        {pulse.deployed} deployed · {pulse.running} running · {pulse.runs24h} ledgered run
        {pulse.runs24h === 1 ? '' : 's'} in 24h
        {pulse.lastRunAt ? ` · last ${fmtRelative(pulse.lastRunAt)}` : ''}
        {' — '}
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <ShieldCheck size={11} aria-hidden="true" /> governed: every run lands in the ledger,
          writes need approval.
        </span>
      </p>

      {agents.length === 0 ? (
        <div className="t-meta" style={{ marginTop: 12, color: C.textMuted }}>
          No agents deployed yet — launch one from a template under Projects → Agents.
        </div>
      ) : (
        <ol
          style={{
            listStyle: 'none',
            margin: '12px 0 0',
            padding: '12px 0 0',
            borderTop: '1px solid rgba(255,255,255,0.08)',
            display: 'grid',
            gap: 8,
          }}
        >
          {listed.map((a) => {
            const s = summaries[a.id];
            const active = s?.activeNow ?? false;
            const latest = s?.recent[0];
            return (
              <li key={a.id} style={{ display: 'flex', alignItems: 'baseline', gap: 8, minWidth: 0 }}>
                <span
                  aria-hidden="true"
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: 99,
                    flex: '0 0 auto',
                    alignSelf: 'center',
                    background: active ? C.green : a.state === 'running' ? C.magenta : 'rgba(255,255,255,0.25)',
                    animation: active ? 'pulse 1.6s ease-in-out infinite' : undefined,
                  }}
                />
                <span className="t-body" style={{ color: C.text, fontWeight: 600, flex: '0 0 auto' }}>
                  {a.name}
                </span>
                <span
                  className="t-meta"
                  style={{
                    color: C.textMuted,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {active && (
                    <span style={{ color: C.green, fontWeight: 700 }}>Active now · </span>
                  )}
                  {latest
                    ? `${fmtRelative(latest.at)} — ${latest.text}`
                    : a.state === 'running'
                      ? 'Running — no ledgered runs yet'
                      : 'Never run'}
                </span>
                {s && s.runCount > 0 && (
                  <span
                    className="t-meta"
                    style={{
                      marginLeft: 'auto',
                      flex: '0 0 auto',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      color: C.textMuted,
                    }}
                  >
                    <Activity size={10} aria-hidden="true" /> {s.runCount}
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </GlassPanel>
  );
}
